import { webmFixDuration } from 'webm-fix-duration'
import { InterruptedRecordingStore } from './interrupted_recording_store.js'
import { InterruptedRecordingManager } from './interrupted_recording_manager.js'
import { persistenceKey } from './interrupted_recording_helpers.js'

const FLUSH_INTERVAL = 500 // ms — how often unsaved chunks are persisted to IndexedDB

/**
 * VoiceRecordingService - Handles audio recording with MediaRecorder API.
 *
 * Interrupted-recording recovery is owned here. EVERY recording is persisted to
 * IndexedDB as it's captured — a recording must never be lost just because its
 * surface didn't opt in — and stays recoverable from the global Interrupted
 * recordings list until the consumer confirms the server received it
 * (commitConsumed(), only after a durable-save acknowledgement — not on mere
 * submit, and not on soft 200/{alert} failures).
 *
 * `restoreKey` is optional and only adds the in-place restore toast: pass it
 * (+ `restoreToastTemplate` and `onRestore`) and the service offers a restore
 * toast on construction and routes a restored blob back through `onRestore`.
 * It needs a stable per-surface anchor; without one, recovery is still available
 * through the global list.
 */
export class VoiceRecordingService {
  constructor(options = {}) {
    this.microphoneManager = options.microphoneManager
    this.mimeType = options.mimeType || this.#getPreferredMimeType()
    this.dataInterval = options.dataInterval || 100 // ms
    this.restoreKey = options.restoreKey || null // only adds the in-place restore toast; persistence always runs
    this.onRestore = options.onRestore || null

    // Recording state
    this.mediaRecorder = null
    this.isRecording = false
    this.isPaused = false
    this.audioChunks = []
    this.startTime = null
    this.pausedDuration = 0
    this.pauseStartTime = null

    // Durable persistence (runs for every recording; restoreKey only adds the toast)
    this.sessionId = null
    this.#consumedSessionIds = new Set() // handed to the consumer, awaiting commitConsumed()
    this.#unflushed = []
    this.#flushTimer = null

    // Event listeners
    this.eventListeners = new Map()

    this.#startRecovery(options.restoreToastTemplate)
  }

  #unflushed
  #flushTimer
  #consumedSessionIds
  #unloadHandler = null
  #visibilityHandler = null
  #beforeUnloadHandler = null
  // Bumped by destroy(), so a start() still awaiting the mic can tell it was torn
  // down in the meantime (e.g. disconnected mid-acquire) and must not go on to record.
  #teardownGeneration = 0

  /**
   * Start recording audio
   * @returns {Promise<boolean>} Success status
   */
  async start() {
    const generation = this.#teardownGeneration
    try {
      const stream = await this.microphoneManager.acquire()
      if (generation !== this.#teardownGeneration) {
        await this.microphoneManager.release()
        return false
      }
      if (!stream || this.isRecording) {
        return false
      }

      this.mediaRecorder = new MediaRecorder(stream, {
        mimeType: this.mimeType,
      })

      this.audioChunks = []
      this.isRecording = true
      this.isPaused = false
      this.startTime = Date.now()
      this.pausedDuration = 0

      await this.#beginPersistence()
      if (generation !== this.#teardownGeneration) {
        // destroy() ran while persistence was starting: it already reset state,
        // removed the page listeners and released the mic. Nothing was captured,
        // so drop the empty session (and the flush timer begun after destroy()).
        // Best-effort: a failed delete leaves an empty session for the store's
        // pruning, and must not surface as an error to a torn-down consumer.
        try {
          await this.#discardPersistence()
        } catch (error) {
          console.error('Discarding abandoned recording session failed:', error)
        }
        return false
      }

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data)
          this.#unflushed.push(event.data)
        }
      }

      this.mediaRecorder.onstart = () => {
        this.#dispatchEvent('started')
      }

      this.mediaRecorder.onstop = async () => {
        const blob = await this.getCurrentAudioBlobWithFixedDuration()
        // This finished recording stays recoverable until commitConsumed() runs,
        // i.e. until the consumer has durably saved it.
        const sessionId = this.sessionId
        if (sessionId) this.#consumedSessionIds.add(sessionId)
        this.#dispatchEvent('stopped', {
          blob,
          duration: this.getDuration(),
          sessionId,
        })
      }

      this.mediaRecorder.start(this.dataInterval)
      return true
    } catch (error) {
      this.isRecording = false
      this.#removeBeforeUnloadWarning()
      this.#dispatchEvent('error', { error })
      return false
    }
  }

  /**
   * Stop recording audio
   */
  async stop() {
    if (this.mediaRecorder && this.isRecording) {
      if (this.isPaused) {
        this.resume() // Resume before stopping to ensure proper cleanup
      }
      this.mediaRecorder.stop()
      this.mediaRecorder = null
      this.isRecording = false
      this.isPaused = false
      // The recording is no longer "in progress" once stopped (a stopped-but-
      // unsent recording is a separate concern — see service.test.mjs) — remove
      // the warning now rather than leaving it inert-but-attached until some
      // later destroy(), so a consumer that drops the service after a
      // successful send (without calling destroy()) doesn't accumulate one
      // stale listener per record-and-submit cycle.
      this.#removeBeforeUnloadWarning()

      await this.#finishPersistence()

      // Release microphone reference
      await this.microphoneManager.release()
    }
  }

  /**
   * Pause recording
   */
  pause() {
    if (this.mediaRecorder && this.isRecording && !this.isPaused) {
      this.mediaRecorder.pause()
      this.isPaused = true
      this.pauseStartTime = Date.now()
      this.#dispatchEvent('paused')
    }
  }

  /**
   * Resume recording
   */
  resume() {
    if (this.mediaRecorder && this.isRecording && this.isPaused) {
      this.mediaRecorder.resume()
      this.isPaused = false
      if (this.pauseStartTime) {
        this.pausedDuration += Date.now() - this.pauseStartTime
        this.pauseStartTime = null
      }
      this.#dispatchEvent('resumed')
    }
  }

  /**
   * Cancel recording: tear everything down (destroy) AND discard the persisted audio,
   * so the recording is not recoverable. Use destroy() alone to keep it for recovery.
   */
  async cancel() {
    await this.destroy()
    await this.#discardPersistence()
    this.#dispatchEvent('canceled')
  }

  /**
   * Flag a finished session so reconnect can silently re-transcribe (and optionally
   * auto-submit) if the tab is killed mid-transcription — common on iOS lock.
   * Call when transcription starts; clear with clearPendingResume() on success.
   */
  async markPendingResume(sessionId, { autoSubmit = false } = {}) {
    const id = sessionId || this.sessionId
    if (!id) return
    try {
      await InterruptedRecordingStore.markPendingResume(id, { autoSubmit })
    } catch (error) {
      console.error('markPendingResume failed:', error)
    }
  }

  async clearPendingResume(sessionId = null) {
    const ids = sessionId
      ? [sessionId]
      : [...this.#consumedSessionIds, this.sessionId].filter(Boolean)
    await Promise.all(
      [...new Set(ids)].map(async (id) => {
        try {
          await InterruptedRecordingStore.clearPendingResume(id)
        } catch (error) {
          console.error('clearPendingResume failed:', error)
        }
      })
    )
  }

  /**
   * Drop the stored copies of every recording the consumer has taken (via the
   * 'stopped' event or onRestore) since the last commit. Call this ONLY once the
   * data is durably saved — until then the recordings stay recoverable.
   */
  async commitConsumed() {
    if (this.#consumedSessionIds.size === 0) return

    // Drop each session from tracking only once its store deletion succeeds, so a
    // failed delete stays tracked and is retried on the next commit rather than
    // lingering as a phantom interrupted recording. Guarded like the rest of our
    // best-effort persistence.
    await Promise.all(
      [...this.#consumedSessionIds].map(async (id) => {
        try {
          await InterruptedRecordingStore.deleteSession(id)
          this.#consumedSessionIds.delete(id)
          if (this.sessionId === id) this.sessionId = null
        } catch (error) {
          console.error('Committed recording cleanup failed:', error)
        }
      })
    )
  }

  /**
   * Get current audio blob
   */
  getCurrentAudioBlob() {
    if (!this.audioChunks || this.audioChunks.length === 0) {
      return null
    }
    return new Blob(this.audioChunks, { type: this.mimeType })
  }

  /**
   * Get current audio blob with fixed duration metadata
   */
  async getCurrentAudioBlobWithFixedDuration() {
    let blob = this.getCurrentAudioBlob()

    if (this.mimeType.includes('webm')) {
      const duration = this.getDuration()
      blob = await webmFixDuration(blob, duration, this.mimeType)
    }

    return blob
  }

  /**
   * Get recording duration in milliseconds
   */
  getDuration() {
    if (!this.startTime) return 0

    const now =
      this.isPaused && this.pauseStartTime ? this.pauseStartTime : Date.now()
    return now - this.startTime - this.pausedDuration
  }

  /**
   * Create a File object from the current recording
   * @param {string} filename - Name for the file (without extension)
   */
  createAudioFile(filename = 'recording') {
    const blob = this.getCurrentAudioBlob()
    if (!blob) return null

    const fullFilename = filename + this.#getFileExtension()
    return new File([blob], fullFilename, { type: this.mimeType })
  }

  /**
   * Create a File object from the current recording with fixed duration metadata
   * @param {string} filename - Name for the file (without extension)
   */
  async createAudioFileWithFixedDuration(filename = 'recording') {
    const blob = await this.getCurrentAudioBlobWithFixedDuration()
    if (!blob) return null

    const fullFilename = filename + this.#getFileExtension()
    return new File([blob], fullFilename, { type: this.mimeType })
  }

  /**
   * Set audio file on a hidden input element for form submission
   * @param {HTMLInputElement} inputElement - File input element
   * @param {string} filename - Name for the file (without extension)
   */
  setFileOnInput(inputElement, filename = 'recording') {
    const audioFile = this.createAudioFile(filename)
    if (!audioFile || !inputElement) return false

    const dataTransfer = new DataTransfer()
    dataTransfer.items.add(audioFile)
    inputElement.files = dataTransfer.files
    return true
  }

  /**
   * Set audio file on a hidden input element for form submission but with fixed duration. Note: this is async, while the unfixed one isn't.
   * @param {HTMLInputElement} inputElement - File input element
   * @param {string} filename - Name for the file (without extension)
   */
  async setFileWithFixedDurationOnInput(inputElement, filename = 'recording') {
    const audioFile = await this.createAudioFileWithFixedDuration(filename)
    if (!audioFile || !inputElement) return false

    const dataTransfer = new DataTransfer()
    dataTransfer.items.add(audioFile)
    inputElement.files = dataTransfer.files
    return true
  }

  /**
   * Add event listener
   * @param {string} event - Event name (started, paused, resumed, stopped, canceled, error)
   * @param {Function} callback - Callback function
   */
  on(event, callback) {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set())
    }
    this.eventListeners.get(event).add(callback)
  }

  /**
   * Remove event listener
   * @param {string} event - Event name
   * @param {Function} callback - Callback function
   */
  off(event, callback) {
    if (this.eventListeners.has(event)) {
      this.eventListeners.get(event).delete(callback)
    }
  }

  /**
   * Tear down on disconnect (e.g. Turbo/AJAX navigation) without losing the recording:
   * stop the recorder, flush the last chunks, and release the mic — leaving the
   * IndexedDB session intact for later recovery. Dispatches 'destroyed'.
   */
  async destroy() {
    this.#teardownGeneration++
    if (this.mediaRecorder && this.isRecording) {
      this.mediaRecorder.onstop = null // suppress the 'stopped' event; this isn't a normal stop
      // Still inactive when destroyed mid-start(), and older browsers throw on stop() then.
      if (this.mediaRecorder.state !== 'inactive') this.mediaRecorder.stop()
      this.mediaRecorder = null
      this.isRecording = false
      this.isPaused = false
      this.audioChunks = []
      clearInterval(this.#flushTimer)
      await this.#flush()
      await this.microphoneManager.release()
    }
    this.#removeUnloadFlush()
    this.#removeBeforeUnloadWarning()
    this.#dispatchEvent('destroyed')
  }

  // Private methods

  #getPreferredMimeType() {
    if (MediaRecorder.isTypeSupported('audio/mp4')) {
      return 'audio/mp4'
    } else {
      return 'audio/webm'
    }
  }

  #getFileExtension() {
    if (this.mimeType.includes('mp4')) {
      return '.mp4'
    } else {
      return '.webm'
    }
  }

  #dispatchEvent(eventName, data = {}) {
    if (this.eventListeners.has(eventName)) {
      this.eventListeners.get(eventName).forEach((callback) => {
        try {
          callback(data)
        } catch (error) {
          console.error(
            `Error in recording service event listener for ${eventName}:`,
            error
          )
        }
      })
    }
  }

  // Marks the restored session as consumed before handing the blob to onRestore,
  // so the restore path matches the live 'stopped' path (both end in commitConsumed()).
  #startRecovery(restoreToastTemplate) {
    if (!this.restoreKey || !restoreToastTemplate || !this.onRestore) return

    new InterruptedRecordingManager({
      key: this.restoreKey,
      toastTemplate: restoreToastTemplate,
      onRestore: (sessionId, blob, options = {}) => {
        this.#consumedSessionIds.add(sessionId)
        this.sessionId = sessionId
        return this.onRestore(blob, { sessionId, ...options })
      },
    }).connect()
  }

  // Persistence is best-effort and guarded — a storage failure (private browsing,
  // quota) must never break recording. It runs for EVERY recording: one with no
  // restoreKey is saved under the shared unkeyed key and recovered from the global
  // Interrupted recordings list instead of an in-place toast.

  async #beginPersistence() {
    const key = persistenceKey(this.restoreKey)
    this.sessionId = `${key}:${this.startTime}`
    this.#unflushed = []
    // Register the unload flush and the reload/close warning lazily, only once a
    // recording actually starts, so a service that's constructed but never
    // records adds no page listeners.
    this.#registerUnloadFlush()
    this.#registerBeforeUnloadWarning()
    try {
      await InterruptedRecordingStore.beginSession({
        sessionId: this.sessionId,
        key,
        mimeType: this.mimeType,
        startedAt: this.startTime,
      })
      this.#flushTimer = setInterval(() => this.#flush(), FLUSH_INTERVAL)
      this.#pruneStore()
    } catch (error) {
      console.error('Voice recording persistence unavailable:', error)
      this.sessionId = null
      // Let the consumer warn the user that this recording can't be recovered
      // (e.g. private browsing blocks IndexedDB).
      this.#dispatchEvent('persistence-unavailable', { error })
    }
  }

  // Bound stored recordings on every surface that records — not just those that
  // mount a recovery toast — so our own retention + oldest-first cap enforcement
  // runs instead of the browser's opaque quota eviction, which could otherwise
  // drop a *recent* recording under storage pressure. Fire-and-forget so it never
  // delays capture; the just-created session is newest, so the cap never evicts it.
  #pruneStore() {
    Promise.resolve()
      .then(() => InterruptedRecordingStore.purgeExpired())
      .then(() => InterruptedRecordingStore.enforceCap())
      .catch((error) =>
        console.error('Interrupted recording pruning failed:', error)
      )
  }

  async #flush() {
    if (!this.sessionId || this.#unflushed.length === 0) return

    const pending = this.#unflushed
    this.#unflushed = []
    try {
      await InterruptedRecordingStore.appendChunks(
        this.sessionId,
        pending,
        this.getDuration()
      )
    } catch (error) {
      console.error('Voice recording chunk persistence failed:', error)
    }
  }

  // Flush the final chunks; the session stays until commitConsumed() deletes it.
  async #finishPersistence() {
    if (!this.sessionId) return

    clearInterval(this.#flushTimer)
    await this.#flush()
  }

  async #discardPersistence() {
    if (!this.sessionId) return

    clearInterval(this.#flushTimer)
    this.#consumedSessionIds.delete(this.sessionId)
    await InterruptedRecordingStore.deleteSession(this.sessionId)
    this.sessionId = null
    this.#unflushed = []
  }

  // Best-effort flush of the last unpersisted chunks when the tab is closed or
  // backgrounded mid-recording. disconnect()/destroy() may not run on a hard
  // close, and the periodic flush only fires every FLUSH_INTERVAL.
  #registerUnloadFlush() {
    // Idempotent: repeated start/stop cycles on one instance must not stack
    // handlers. Torn down in destroy() (removeUnloadFlush clears #unloadHandler).
    if (this.#unloadHandler) return

    this.#unloadHandler = () => this.#flush()
    this.#visibilityHandler = () => {
      if (document.visibilityState === 'hidden') this.#flush()
    }
    window.addEventListener('pagehide', this.#unloadHandler)
    document.addEventListener('visibilitychange', this.#visibilityHandler)
  }

  #removeUnloadFlush() {
    if (this.#unloadHandler) {
      window.removeEventListener('pagehide', this.#unloadHandler)
    }
    if (this.#visibilityHandler) {
      document.removeEventListener('visibilitychange', this.#visibilityHandler)
    }
    this.#unloadHandler = null
    this.#visibilityHandler = null
  }

  // Warn before an accidental reload/close drops an in-progress recording.
  // Removed in both stop() (the recording is no longer "in progress" once
  // stopped) and destroy() (torn down without an explicit stop(), e.g. a
  // disconnect mid-recording) — covering every teardown path without relying
  // on any one consumer remembering to call destroy() after a successful
  // send. The isRecording check below is a second line of defense in case
  // this ever gets called while still attached.
  #registerBeforeUnloadWarning() {
    if (this.#beforeUnloadHandler) return

    this.#beforeUnloadHandler = (event) => {
      if (!this.isRecording) return
      event.preventDefault()
      // Legacy browsers ignore preventDefault() here and only prompt on a truthy returnValue.
      event.returnValue = true
    }
    window.addEventListener('beforeunload', this.#beforeUnloadHandler)
  }

  #removeBeforeUnloadWarning() {
    if (this.#beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.#beforeUnloadHandler)
    }
    this.#beforeUnloadHandler = null
  }
}
